import React, { useRef, useEffect, useState, useLayoutEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Menu } from "../components/Menus/Menu";
import { ConfiguratorImages } from "../components/Image/ConfiguratorImages";
import { MainMenuButtons } from "../components/Menus/MainMenuButtons";
import { Summary } from "../components/Summary/Summary";
import EventCatcher from "../components/EventCatcher/EventCatcher";

import { useBreakpoint } from "../utilities/media-js-breakpoints";
import Constants from "../constants";

import { getMenuItems, getPreviewItem, getSelectedMenu, getShowCancelButton } from "../store/sagas/selectors";
import { FrameButtons } from "../components/ViewControls/FrameButtons";
import { MobileMenuArea } from "../components/Menus/MobileMenuArea";
import { DesktopReadmoreCard } from "../components/Readmore/DesktopReadmoreCard";
import { MobileContinueButton } from "../components/NavigationButtons/MobileContinueButton";
import { MobileSummaryButton } from "../components/NavigationButtons/MobileSummaryButton";
import { MobileCancelButton } from "../components/NavigationButtons/MobileCancelButton";
import { CALL_TO_ACTION } from "../store/actions/externalActions";
import { ExternalMessages } from "../externalMessages";
import GaReporter from "../utilities/gaReporter";

const Configurator = () => {
    const breakpoints = useBreakpoint();
    const dispatch = useDispatch();

    const menuRef = useRef<any>(null);
    const scrollbarRef = useRef<any>(null);
    const currentMenuTypeRef = useRef<any>(null);
    const summaryRef = useRef<any>();

    const items = useSelector(getMenuItems);
    const selectedMenu = useSelector(getSelectedMenu);
    const previewItem = useSelector(getPreviewItem);
    const showCancelButton = useSelector(getShowCancelButton);

    const [menuWidth, setMenuWidth] = useState(100);
    const [menuDynamicType, setMenuDynamicType] = useState("");

    const menuItems = selectedMenu ? items[selectedMenu] : null;

    const setCurrentMenuTypeRef = (ref: any) => {
        currentMenuTypeRef.current = ref;
    };

    useEffect(() => {
        if (menuRef.current) setMenuWidth(menuRef.current.clientWidth);
    }, [menuRef]);

    useLayoutEffect(() => {
        if (!menuItems) return;

        let shouldScrollToSelectedItem = true;
        menuItems.some((el: any) => {
            if (el.dynamicType) {
                if (!currentMenuTypeRef.current) {
                    setMenuDynamicType(el.dynamicType);
                    setCurrentMenuTypeRef(el);
                    return true;
                }

                shouldScrollToSelectedItem = !(menuDynamicType === el.dynamicType) && el.dynamicType !== "GENERIC";
                setMenuDynamicType(el.dynamicType);
                setCurrentMenuTypeRef(el);
                return true;
            }
            return false;
        });

        if (!shouldScrollToSelectedItem) return;

        let i = 0;
        menuItems.some((e: any) => {
            i += 1;
            if (e.isSelected) {
                return true;
            }
            return false;
        });

        if (scrollbarRef.current) {
            const priceHeight = currentMenuTypeRef.current.price?.text ? Constants.menuItemPriceHeight : 0;
            const cellWidth = Constants.menuItemWidth + Constants.menuItemMarginRight;
            const cellHeight = Constants.menuItemHeight + priceHeight + Constants.menuItemmargeinBottom;
            const columns = Math.floor((menuWidth - 42) / cellWidth);
            const rowPos = Math.ceil(i / columns);
            const currentPos = rowPos * cellHeight - scrollbarRef.current.view.offsetHeight / 2 - cellHeight / 2;

            scrollbarRef.current.scrollTop(currentPos);
        }
    }, [menuItems, menuWidth, menuDynamicType]);

    const onBackdropClick = useCallback(() => summaryRef.current?.setExpanded(false), [summaryRef]);

    const onSummaryContinue = () => {
        GaReporter.event("summary_continue", "CTA");
        dispatch({ type: CALL_TO_ACTION, action: ExternalMessages.SUMMARY_CONTINUE });
    };

    if (!breakpoints.landscape) {
        return (
            <>
                <ConfiguratorImages />
                <EventCatcher onBackdropClick={onBackdropClick} />
                <MobileMenuArea />
                <MobileContinueButton onContinue={onSummaryContinue} />
                <MobileSummaryButton />
                {showCancelButton && <MobileCancelButton />}
                <FrameButtons />
            </>
        );
    }

    return (
        <>
            <ConfiguratorImages />
            <EventCatcher onBackdropClick={onBackdropClick} />
            <MainMenuButtons />
            {previewItem ? <DesktopReadmoreCard key={previewItem.id} item={previewItem} /> : null}
            <Summary ref={summaryRef} onContinue={onSummaryContinue} />
            <Menu items={menuItems} selectedMenu={selectedMenu} />
            <FrameButtons />
        </>
    );
};

export default Configurator;
